import { useState, useEffect } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { examQuestionAPI, uploadAPI } from '../services/api';
import './Brands.css';
import Icon from '../components/Icon';

function ExamQuestions() {
  const [questions, setQuestions] = useState([]);
  const [states, setStates] = useState([]);
  const [locales, setLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncResults, setSyncResults] = useState([]);
  const [syncComplete, setSyncComplete] = useState(false);
  const [showSyncOptions, setShowSyncOptions] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [hasPendingSync, setHasPendingSync] = useState(false);

  // Pagination state
  const [page, setPage] = useUrlState('page', 1);
  const [limit, setLimit] = useUrlState('limit', 10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [localeFilter, setLocaleFilter] = useState('');

  // Form modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    stateId: '',
    locale: '',
    questionType: 'SINGLE_CHOICE',
    question: '',
    image: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctAnswer: 'A'
  });
  const [formLoading, setFormLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  // Check sync progress on load
  const checkSyncProgress = async () => {
    try {
      const response = await examQuestionAPI.getSyncProgress();
      const progress = response.data;
      setSyncProgress(progress);
      setHasPendingSync(progress && progress.status === 'in_progress');
    } catch (err) {
      console.error('Failed to check sync progress:', err);
    }
  };

  // Fetch states
  const fetchStates = async () => {
    try {
      const response = await examQuestionAPI.getStates();
      setStates(response.data || []);
    } catch (err) {
      console.error('Failed to fetch states:', err);
    }
  };

  // Fetch locales based on state
  const fetchLocales = async (stateId) => {
    try {
      const params = stateId ? { stateId } : {};
      const response = await examQuestionAPI.getLocales(params);
      setLocales(response.data || []);
    } catch (err) {
      console.error('Failed to fetch locales:', err);
    }
  };

  const fetchQuestions = async (currentPage, currentLimit, currentSearch, currentState, currentLocale) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: currentLimit
      };
      if (currentSearch) params.search = currentSearch;
      if (currentState) params.stateId = currentState;
      if (currentLocale) params.locale = currentLocale;

      const response = await examQuestionAPI.getQuestions(params);

      if (Array.isArray(response.data)) {
        setQuestions(response.data);
        setTotal(response.data.length);
        setTotalPages(1);
      } else {
        setQuestions(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
      setError('');
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch exam questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStates();
    checkSyncProgress();
  }, []);

  useEffect(() => {
    fetchLocales(stateFilter);
  }, [stateFilter]);

  useEffect(() => {
    fetchQuestions(page, limit, search, stateFilter, localeFilter);
  }, [page, limit, search, stateFilter, localeFilter]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this exam question?')) {
      try {
        await examQuestionAPI.deleteQuestion(id);
        fetchQuestions(page, limit, search, stateFilter, localeFilter);
      } catch (err) {
        setError('Failed to delete exam question');
      }
    }
  };

  // Form modal handlers
  const handleCreate = () => {
    setEditingQuestion(null);
    setFormData({
      stateId: stateFilter || '',
      locale: localeFilter || '',
      questionType: 'SINGLE_CHOICE',
      question: '',
      image: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctAnswer: 'A'
    });
    setImagePreview('');
    setShowFormModal(true);
  };

  const handleEdit = async (q) => {
    setEditingQuestion(q);
    setFormData({
      stateId: q.stateId || '',
      locale: q.locale || '',
      questionType: q.questionType || 'SINGLE_CHOICE',
      question: q.question || '',
      image: q.image || '',
      optionA: q.optionA || '',
      optionB: q.optionB || '',
      optionC: q.optionC || '',
      optionD: q.optionD || '',
      correctAnswer: q.correctAnswer || 'A'
    });
    setImagePreview(q.image || '');
    setShowFormModal(true);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File is too large. Maximum size is 5MB.');
      return;
    }

    setImageUploading(true);
    setError('');

    try {
      const response = await uploadAPI.uploadQuestionImage(file);
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image: response.data.url, questionType: 'SINGLE_CHOICE_IMAGE' }));
        setImagePreview(response.data.url);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  // Remove uploaded image
  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image: '', questionType: 'SINGLE_CHOICE' }));
    setImagePreview('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.stateId || !formData.locale || !formData.question || !formData.optionA || !formData.optionB) {
      setError('State, Language, Question, Option A and Option B are required');
      return;
    }

    setFormLoading(true);
    try {
      if (editingQuestion) {
        await examQuestionAPI.updateQuestion(editingQuestion.id, formData);
      } else {
        await examQuestionAPI.createQuestion(formData);
      }
      setShowFormModal(false);
      setEditingQuestion(null);
      fetchQuestions(page, limit, search, stateFilter, localeFilter);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save exam question');
    } finally {
      setFormLoading(false);
    }
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingQuestion(null);
    setImagePreview('');
  };

  // Sync questions for selected state and locale
  const handleSyncSelected = async () => {
    if (!stateFilter || !localeFilter) {
      setError('Please select a state and language first');
      return;
    }

    setShowSyncOptions(false);
    setSyncing(true);
    setShowSyncModal(true);
    setSyncComplete(false);
    setSyncResults([]);

    try {
      const response = await examQuestionAPI.syncQuestions(stateFilter, localeFilter);
      const data = response.data;

      if (data.success) {
        setSyncResults([{
          state: data.stateName,
          locale: data.locale,
          count: data.totalSynced
        }]);
      } else {
        setError(data.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError(err.response?.data?.error || 'Failed to sync exam questions');
    }

    setSyncComplete(true);
    setSyncing(false);
    fetchQuestions(page, limit, search, stateFilter, localeFilter);
  };

  // Sync all exam questions for all states and languages
  const handleSyncAll = async (mode = 'smart') => {
    setShowSyncOptions(false);
    setSyncing(true);
    setShowSyncModal(true);
    setSyncComplete(false);
    setSyncResults([]);

    try {
      const response = await examQuestionAPI.syncAll(mode);
      const data = response.data;

      if (data.success) {
        setSyncResults(data.results || []);
      } else {
        setError(data.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError(err.response?.data?.error || 'Failed to sync exam questions');
    }

    setSyncComplete(true);
    setSyncing(false);
    setHasPendingSync(false);
    setSyncProgress(null);
    fetchStates();
    fetchQuestions(page, limit, search, stateFilter, localeFilter);
  };

  // Resume sync from where it left off
  const handleResumeSync = async () => {
    handleSyncAll('resume');
  };

  // Reset sync progress
  const handleResetSync = async () => {
    if (window.confirm('Are you sure you want to reset sync progress? This will start fresh on the next sync.')) {
      try {
        await examQuestionAPI.resetSyncProgress();
        setHasPendingSync(false);
        setSyncProgress(null);
      } catch (err) {
        console.error('Failed to reset sync progress:', err);
        setError('Failed to reset sync progress');
      }
    }
  };

  const closeSyncModal = () => {
    setShowSyncModal(false);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleStateFilterChange = (e) => {
    setStateFilter(e.target.value);
    setLocaleFilter(''); // Reset locale when state changes
    setPage(1);
  };

  const handleLocaleFilterChange = (e) => {
    setLocaleFilter(e.target.value);
    setPage(1);
  };

  const getTotalSynced = () => {
    return syncResults.reduce((sum, r) => sum + (r.count || 0), 0);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${page === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
          >
            First
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
          >
            Prev
          </button>
          {pages}
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="brands-page">
      {/* Sync Progress Modal */}
      {showSyncModal && (
        <div className="modal-overlay">
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{syncComplete ? 'Sync Complete' : 'Syncing Exam Questions...'}</h2>
              {syncComplete && <button className="modal-close" onClick={closeSyncModal}>&times;</button>}
            </div>
            <div className="modal-body sync-modal-body">
              {syncing && (
                <div className="sync-progress">
                  <div className="spinner"></div>
                  <p className="sync-status">
                    <strong>Fetching exam questions from API...</strong>
                  </p>
                  <p className="sync-counts">
                    This may take several minutes for all states and languages
                  </p>
                  <div className="progress-bar">
                    <div className="progress-fill indeterminate" style={{ width: '30%' }}></div>
                  </div>
                  <p className="progress-percent">Please wait...</p>
                </div>
              )}

              {syncComplete && syncResults.length > 0 && (
                <div className="sync-results">
                  <h3>Total: {getTotalSynced()} exam questions synced</h3>
                  <div className="results-grid">
                    {syncResults.filter(r => r.count > 0).map((result, index) => (
                      <div key={index} className="result-item success">
                        <span className="state-name">{result.state} {result.locale ? `(${result.languageName || result.locale})` : ''}</span>
                        <span className="count">{result.count} questions</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {syncComplete && syncResults.length === 0 && (
                <div className="sync-results">
                  <h3>No new data synced</h3>
                  <p>All exam questions are already up to date.</p>
                </div>
              )}
            </div>
            {syncComplete && (
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={closeSyncModal}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showFormModal && (
        <div className="modal-overlay" onClick={closeFormModal}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingQuestion ? 'Edit Exam Question' : 'Add Exam Question'}</h2>
              <button className="modal-close" onClick={closeFormModal}>&times;</button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>State *</label>
                    <select
                      name="stateId"
                      value={formData.stateId}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map(state => (
                        <option key={state.id} value={state.id}>
                          {state.stateName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Language *</label>
                    <select
                      name="locale"
                      value={formData.locale}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select Language</option>
                      {locales.map(locale => (
                        <option key={locale.locale} value={locale.locale}>
                          {locale.languageName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Question *</label>
                  <textarea
                    name="question"
                    value={formData.question}
                    onChange={handleFormChange}
                    placeholder="Enter question text"
                    rows="3"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Question Image (Optional)</label>
                  <div className="image-upload-container">
                    {imagePreview ? (
                      <div className="image-preview-wrapper">
                        <img src={imagePreview.startsWith('http') ? imagePreview : `${window.location.origin}${imagePreview}`} alt="Question" className="image-preview" />
                        <button type="button" className="btn btn-danger btn-sm remove-image-btn" onClick={handleRemoveImage}>
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="image-upload-area">
                        <input
                          type="file"
                          id="questionImage"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="questionImage" className="upload-label">
                          {imageUploading ? (
                            <span>Uploading...</span>
                          ) : (
                            <>
                              <span className="upload-icon"><Icon name="camera" size={22} /></span>
                              <span>Click to upload image</span>
                              <span className="upload-hint">JPEG, PNG, GIF, WebP (max 5MB)</span>
                            </>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Option A *</label>
                    <input
                      type="text"
                      name="optionA"
                      value={formData.optionA}
                      onChange={handleFormChange}
                      placeholder="Enter option A"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Option B *</label>
                    <input
                      type="text"
                      name="optionB"
                      value={formData.optionB}
                      onChange={handleFormChange}
                      placeholder="Enter option B"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Option C</label>
                    <input
                      type="text"
                      name="optionC"
                      value={formData.optionC}
                      onChange={handleFormChange}
                      placeholder="Enter option C (optional)"
                    />
                  </div>
                  <div className="form-group">
                    <label>Option D</label>
                    <input
                      type="text"
                      name="optionD"
                      value={formData.optionD}
                      onChange={handleFormChange}
                      placeholder="Enter option D (optional)"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Correct Answer *</label>
                  <select
                    name="correctAnswer"
                    value={formData.correctAnswer}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeFormModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingQuestion ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="page-header">
        <h1>Exam Questions</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleCreate}>
            + Add Question
          </button>
          <div className="sync-dropdown">
            <button
              className={`btn btn-sync ${hasPendingSync ? 'has-pending' : ''}`}
              onClick={() => setShowSyncOptions(!showSyncOptions)}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : hasPendingSync ? 'Resume Available' : 'Sync Data'} ▼
            </button>
            {showSyncOptions && (
              <div className="sync-options-menu">
                {stateFilter && localeFilter && (
                  <button onClick={handleSyncSelected} className="sync-option">
                    <strong>Sync Selected</strong>
                    <span>Sync questions for selected state & language</span>
                  </button>
                )}
                {hasPendingSync && (
                  <>
                    <button onClick={handleResumeSync} className="sync-option resume-option">
                      <strong>Resume Sync</strong>
                      <span>Continue from {syncProgress?.lastCityName || 'last position'} ({syncProgress?.processedCities || 0} completed)</span>
                    </button>
                    <button onClick={handleResetSync} className="sync-option reset-option">
                      <strong>Reset Progress</strong>
                      <span>Start fresh on next sync</span>
                    </button>
                  </>
                )}
                <button onClick={() => handleSyncAll('smart')} className="sync-option">
                  <strong>Sync All</strong>
                  <span>Sync all exam questions (requires states synced first)</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="table-controls">
          <div className="table-filters">
            <input
              type="text"
              placeholder="Search questions or options..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
            <select value={stateFilter} onChange={handleStateFilterChange} className="filter-select">
              <option value="">All States</option>
              {states.map(state => (
                <option key={state.id} value={state.id}>
                  {state.stateName} ({state._count?.examQuestions || 0})
                </option>
              ))}
            </select>
            <select value={localeFilter} onChange={handleLocaleFilterChange} className="filter-select">
              <option value="">All Languages</option>
              {locales.map(locale => (
                <option key={locale.locale} value={locale.locale}>
                  {locale.languageName}
                </option>
              ))}
            </select>
          </div>
          <div className="table-options">
            <label>
              Show
              <select value={limit} onChange={handleLimitChange}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              entries
            </label>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : questions.length === 0 ? (
          <div className="empty-state">
            <p>No exam questions found. {search || stateFilter || localeFilter ? 'Try adjusting your filters.' : 'Please sync states from Question Bank first, then sync exam questions!'}</p>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{width: '60px'}}>ID</th>
                  <th style={{width: '70px'}}>Type</th>
                  <th>Question</th>
                  <th style={{width: '150px'}}>Option A</th>
                  <th style={{width: '150px'}}>Option B</th>
                  <th style={{width: '150px'}}>Option C</th>
                  <th style={{width: '150px'}}>Option D</th>
                  <th style={{width: '60px'}}>Ans</th>
                  <th style={{width: '100px'}}>State</th>
                  <th style={{width: '60px'}}>Lang</th>
                  <th style={{width: '80px'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {questions.map((q) => (
                  <tr key={q.id}>
                    <td>{q.questionId}</td>
                    <td>
                      <span className={`type-badge ${q.questionType === 'SINGLE_CHOICE_IMAGE' ? 'type-image' : 'type-text'}`}>
                        {q.questionType === 'SINGLE_CHOICE_IMAGE' ? 'Image' : 'Text'}
                      </span>
                    </td>
                    <td className="question-cell">
                      <div>{q.question}</div>
                      {q.image && (
                        <div className="question-image">
                          <img src={q.image} alt="Question" />
                        </div>
                      )}
                    </td>
                    <td className={`option-cell ${q.correctAnswer === 'A' ? 'correct-answer' : ''}`}>{q.optionA}</td>
                    <td className={`option-cell ${q.correctAnswer === 'B' ? 'correct-answer' : ''}`}>{q.optionB}</td>
                    <td className={`option-cell ${q.correctAnswer === 'C' ? 'correct-answer' : ''}`}>{q.optionC || '-'}</td>
                    <td className={`option-cell ${q.correctAnswer === 'D' ? 'correct-answer' : ''}`}>{q.optionD || '-'}</td>
                    <td className="answer-badge">{q.correctAnswer}</td>
                    <td>{q.state?.stateName || '-'}</td>
                    <td>{q.locale}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(q)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(q.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && renderPagination()}
          </>
        )}
      </div>

      <style jsx>{`
        .sync-dropdown {
          position: relative;
          display: inline-block;
        }
        .btn-sync.has-pending {
          background-color: #ffc107;
          color: #212529;
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(255, 193, 7, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); }
        }
        .sync-options-menu {
          position: absolute;
          top: 100%;
          right: 0;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          min-width: 280px;
          z-index: 1000;
          margin-top: 5px;
        }
        .sync-option {
          display: block;
          width: 100%;
          padding: 12px 16px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          border-bottom: 1px solid #eee;
        }
        .sync-option:last-child {
          border-bottom: none;
        }
        .sync-option:hover {
          background-color: #f5f5f5;
        }
        .sync-option.resume-option {
          background-color: #fff3cd;
          border-left: 3px solid #ffc107;
        }
        .sync-option.resume-option:hover {
          background-color: #ffecb5;
        }
        .sync-option.reset-option {
          background-color: #f8d7da;
          border-left: 3px solid #dc3545;
        }
        .sync-option.reset-option:hover {
          background-color: #f1aeb5;
        }
        .sync-option strong {
          display: block;
          color: #333;
          margin-bottom: 2px;
        }
        .sync-option span {
          display: block;
          font-size: 12px;
          color: #666;
        }
        .sync-progress {
          text-align: center;
          padding: 20px;
        }
        .sync-status {
          margin: 15px 0 5px;
          font-size: 16px;
        }
        .sync-counts {
          color: #666;
          font-size: 14px;
          margin-bottom: 15px;
        }
        .progress-percent {
          margin-top: 8px;
          font-weight: bold;
          color: #007bff;
        }
        .sync-results {
          margin-top: 10px;
        }
        .sync-results h3 {
          margin-bottom: 15px;
          color: #28a745;
          text-align: center;
        }
        .sync-results p {
          text-align: center;
          color: #666;
        }
        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 10px;
          max-height: 300px;
          overflow-y: auto;
        }
        .result-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 15px;
          border-radius: 4px;
          font-size: 14px;
        }
        .result-item.success {
          background-color: #d4edda;
          color: #155724;
        }
        .result-item .state-name {
          font-weight: 500;
        }
        .result-item .count {
          font-size: 12px;
          opacity: 0.8;
        }
        .progress-bar {
          width: 100%;
          height: 10px;
          background-color: #e9ecef;
          border-radius: 5px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          background-color: #007bff;
          transition: width 0.3s ease;
        }
        .progress-fill.indeterminate {
          animation: indeterminate 1.5s infinite ease-in-out;
        }
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(200%); }
          100% { transform: translateX(-100%); }
        }
        .question-cell {
          max-width: 250px;
          white-space: normal;
          line-height: 1.4;
        }
        .option-cell {
          max-width: 150px;
          white-space: normal;
          line-height: 1.3;
          font-size: 13px;
        }
        .option-cell.correct-answer {
          background-color: #d4edda;
          color: #155724;
          font-weight: 500;
        }
        .answer-badge {
          text-align: center;
          font-weight: bold;
          color: #28a745;
        }
        .type-badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .type-badge.type-text {
          background-color: #e7f3ff;
          color: #0056b3;
        }
        .type-badge.type-image {
          background-color: #fff3cd;
          color: #856404;
        }
        .question-image {
          margin-top: 10px;
        }
        .question-image img {
          max-width: 200px;
          max-height: 150px;
          border-radius: 4px;
          border: 1px solid #ddd;
          object-fit: contain;
          cursor: pointer;
          transition: transform 0.2s;
        }
        .question-image img:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }
        .form-modal {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .form-modal .modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px 24px;
          border-radius: 12px 12px 0 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
        }
        .form-modal .modal-header h2 {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 600;
        }
        .form-modal .modal-close {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .form-modal .modal-close:hover {
          background: rgba(255,255,255,0.3);
        }
        .form-modal .modal-body {
          padding: 24px;
        }
        .form-modal .modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .form-group {
          margin-bottom: 16px;
          text-align: left;
        }
        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #333;
          font-size: 14px;
          text-align: left;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .image-upload-container {
          margin-top: 8px;
        }
        .image-upload-area {
          border: 2px dashed #ddd;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          cursor: pointer;
          transition: border-color 0.2s, background-color 0.2s;
        }
        .image-upload-area:hover {
          border-color: #667eea;
          background-color: #f8f9ff;
        }
        .upload-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          color: #666;
        }
        .upload-icon {
          font-size: 32px;
        }
        .upload-hint {
          font-size: 12px;
          color: #999;
        }
        .image-preview-wrapper {
          position: relative;
          display: inline-block;
        }
        .image-preview {
          max-width: 100%;
          max-height: 200px;
          border-radius: 8px;
          border: 1px solid #ddd;
          object-fit: contain;
        }
        .remove-image-btn {
          position: absolute;
          top: 8px;
          right: 8px;
          padding: 4px 8px;
          font-size: 12px;
        }
      `}</style>
    </div>
  );
}

export default ExamQuestions;
